<template>
  <BaseModal
      :cancel-text="''"
      :confirm-text="''"
      :visible="visible"
      variant="sheet"
      @update:visible="(val) => emit('update:visible', val)"
  >
    <div class="flex justify-end -mt-2 -mr-2 md:hidden">
      <button
          :aria-label="t('close')"
          class="inline-flex items-center justify-center rounded-md p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition"
          type="button"
          @click="emit('update:visible', false)"
      >
        <XIcon class="w-4 h-4"/>
      </button>
    </div>
    <div class="w-full">

      <div
          v-if="message"
          class="mb-3 rounded-xl border border-indigo-200/80 bg-indigo-50/90 px-4 py-3 text-start text-sm text-indigo-900 dark:border-indigo-800/60 dark:bg-indigo-950/40 dark:text-indigo-100"
      >
        {{ message }}
      </div>

      <!-- Value header -->
      <div>
        <div
            class="overflow-hidden rounded-2xl border border-gray-200/80 bg-gray-50/80 dark:border-gray-700/80 dark:bg-gray-800/50"
        >
          <div class="flex items-start gap-3 p-3.5 md:p-4">
            <div
                class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm dark:from-amber-500 dark:to-orange-600"
            >
              <UtensilsIcon aria-hidden="true" class="h-5 w-5"/>
            </div>
            <div class="min-w-0 text-gray-900 dark:text-gray-100">
              <h3 class="text-base font-semibold leading-snug md:text-[1.05rem]">{{ $t('auth.titleFreePlan') }}</h3>
              <p class="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300 md:text-sm">{{
                  $t('auth.subtitleFreePlan')
                }}</p>
              <ul class="mt-2.5 space-y-1.5 text-xs leading-snug text-gray-700 dark:text-gray-300 md:text-sm">
                <li class="flex items-start gap-2">
                  <CheckIcon aria-hidden="true" class="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"/>
                  <span>{{ $t('auth.bulletPlannerAnywhere') }}</span>
                </li>
                <li class="flex items-start gap-2">
                  <CheckIcon aria-hidden="true" class="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"/>
                  <span>{{ $t('auth.bulletSaveFavorites') }}</span>
                </li>
                <li class="flex items-start gap-2">
                  <CheckIcon aria-hidden="true" class="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"/>
                  <span>{{ $t('auth.bulletAutoGrocery') }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="pt-4 pb-2">
        <!-- DEFAULT STATE: No intent shown. Primary Google, secondary Email -->
        <div v-if="view === 'default'" class="max-w-md mx-auto">
          <AppleLoginButton v-if="isIos" class="mb-3" />
          <GoogleLoginButton/>
          <p class="mt-2 text-center text-xs text-gray-500 dark:text-gray-400">{{ $t('auth.googleHelper') }}</p>

          <button
              class="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-3.5 text-[15px] font-semibold text-gray-900 shadow-sm transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800/80 dark:text-gray-100 dark:hover:bg-gray-700/80"
              type="button"
              @click="goToEmailIntent"
          >
            <MailIcon class="w-5 h-5"/>
            <span>{{ $t('auth.continue_with_email') }}</span>
          </button>

          <!-- Trust & privacy reassurance footer -->
          <div class="mt-6 text-center">
            <p class="text-[11px] leading-5 text-gray-500 dark:text-gray-400">{{
                $t('auth.privacyReassuranceCombined')
              }}</p>
            <button
                :aria-expanded="showWhy.toString()"
                class="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:underline cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900 rounded"
                type="button"
                @click="showWhy = !showWhy"
            >
              <InfoIcon aria-hidden="true" class="w-3.5 h-3.5"/>
              <span>{{ $t('auth.whyNeedAccount') }}</span>
            </button>
            <div v-if="showWhy" class="mt-2 text-xs text-gray-600 dark:text-gray-300">
              {{ $t('auth.whyNeedAccountExplainer') }}
            </div>
          </div>

          <p class="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">{{
              $t('auth.browseWithoutAccountHint')
            }}</p>
        </div>

        <div v-else-if="view === 'email'" class="mx-auto max-w-md">
          <!-- Segmented login / register -->
          <div
              class="mb-5 flex gap-1 rounded-2xl bg-gray-100/95 p-1 ring-1 ring-black/[0.04] dark:bg-gray-800/90 dark:ring-white/[0.06]"
              role="tablist"
          >
            <button
                :class="[
                  'flex-1 rounded-xl py-2.5 text-center text-sm font-semibold transition-all duration-200',
                  selectedIntent === 'login'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                    : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200',
                ]"
                role="tab"
                :aria-selected="selectedIntent === 'login'"
                type="button"
                @click="selectIntent('login')"
            >
              {{ $t('auth.tabLoginIntent') }}
            </button>
            <button
                :class="[
                  'flex-1 rounded-xl py-2.5 text-center text-sm font-semibold transition-all duration-200',
                  selectedIntent === 'register'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                    : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200',
                ]"
                role="tab"
                :aria-selected="selectedIntent === 'register'"
                type="button"
                @click="selectIntent('register')"
            >
              {{ $t('auth.tabSignupIntent') }}
            </button>
          </div>

          <!-- Forms -->
          <LoginForm
              v-if="selectedIntent === 'login'"
              :is-modal="true"
              @success="onSuccess"
              @switch-tab="onFormSwitch"
              @close-modal="emit('update:visible', false)"
          />
          <RegisterForm
              v-else
              :is-modal="true"
              @success="onSuccess"
              @switch-tab="onFormSwitch"
              @close-modal="emit('update:visible', false)"
          />
        </div>

        <!-- FORGOT PASSWORD (email flow) -->
        <div v-else-if="view === 'forgot-password'" class="mx-auto max-w-md pt-1">
          <ForgotPasswordForm
              :is-modal="true"
              @switch-tab="onFormSwitch"
              @close-modal="emit('update:visible', false)"
          />
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import BaseModal from '@/components/BaseModal.vue'
import LoginForm from '@/components/auth/LoginForm.vue'
import RegisterForm from '@/components/auth/RegisterForm.vue'
import ForgotPasswordForm from '@/components/auth/ForgotPasswordForm.vue'
import GoogleLoginButton from '@/components/auth/GoogleLoginButton.vue'
import AppleLoginButton from '@/components/auth/AppleLoginButton.vue'
import {
  X as XIcon, Mail as MailIcon, CheckCircle2 as CheckIcon, Utensils as UtensilsIcon, Info as InfoIcon
} from 'lucide-vue-next'

const { t } = useI18n()
const props = defineProps({
  visible: { type: Boolean, default: false },
  message: { type: String, default: '' },
  initialTab: { type: String, default: 'login' }
})

const isIos = window.Capacitor && window.Capacitor.getPlatform() === 'ios'

const emit = defineEmits(['update:visible', 'authenticated'])

const auth = useAuthStore()
const route = useRoute()
// View state machine for the modal flow
// 'default' -> Google primary + Email secondary, no intent shown
// 'email' -> email flow with inline tabs (login/register)
// 'forgot-password' -> forgot password form
const view = ref('default')
const selectedIntent = ref('login')
const showWhy = ref(false)

watch(() => props.visible, (val) => {
  if (val) {
    // Reset to calm default state on open
    view.value = 'default'
    selectedIntent.value = 'login'
    showWhy.value = false
    // Save the current URL when the modal is opened
    auth.saveOriginalUrl(route.fullPath)
  }
})

// Handle user switching (e.g., from forms) without exposing tabs globally
function onFormSwitch (next) {
  if (next === 'login') {
    selectedIntent.value = 'login'
    view.value = 'email'
  } else if (next === 'register') {
    selectedIntent.value = 'register'
    view.value = 'email'
  } else if (next === 'forgot-password') {
    view.value = 'forgot-password'
  } else if (next === 'default') {
    view.value = 'default'
  }
}

watch(() => auth.user, (user) => {
  if (user && props.visible) {
    emit('update:visible', false)
    emit('authenticated')
  }
})

function onSuccess () {
  emit('authenticated')
  emit('update:visible', false)
}

function goToEmailIntent () {
  // Per requirement: show tabs with the New Account tab active by default
  selectedIntent.value = 'register'
  view.value = 'email'
}

function selectIntent (intent) {
  selectedIntent.value = intent
  // stay within the 'email' view; form below switches by selectedIntent
}
</script>
